import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import "./myWork.css";

import MyWorkSummaryCards from "./components/MyWorkSummaryCards";
import MyWorkAttentionCards from "./components/MyWorkAttentionCards";
import MyWorkTable from "./components/MyWorkTable";
import wmsMyWorkService from "../services/wmsMyWorkService";

import wmsWorkService
  from "../services/wmsWorkService";

import CommentsModal from "../workspace/components/CommentsModal";

import wmsExecutorWorkspaceService
  from "../workspace/executor/services/wmsExecutorWorkspaceService";


const EMPTY_DATA = {
  summary: {
    execution: 0,
    ownership: 0,
    approvals: 0,
  },

  attention: {
    sla_breached: 0,
    sla_warning: 0,
    due_today: 0,
    escalated: 0,
    pending_approval: 0,
    pending_execution: 0,
  },

  work: [],
  my_escalations: [],
  sla_distribution: [],
  timeline: [],

  pagination: {
    page: 1,
    page_size: 25,
    total: 0,
    total_pages: 0,
    has_previous: false,
    has_next: false,
  },
};

export default function MyWorkPage() {
  const navigate = useNavigate();

  // -------------------------------------------------------------------------
  // TAB
  // -------------------------------------------------------------------------

  const [activeTab, setActiveTab] = useState("ALL");

  // -------------------------------------------------------------------------
  // DATA
  // -------------------------------------------------------------------------

  const [data, setData] = useState(EMPTY_DATA);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  // -------------------------------------------------------------------------
    // COMMENTS
    // -------------------------------------------------------------------------

    const [showComments, setShowComments] =
    useState(false);

    const [selectedWork, setSelectedWork] =
    useState(null);

    const [commentWorkspace, setCommentWorkspace] =
    useState(null);

  // -------------------------------------------------------------------------
  // FILTERS
  // -------------------------------------------------------------------------

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    sla_status: "",
    priority: "",
    work_mode: "",
    template: "",
  });

  // -------------------------------------------------------------------------
  // PAGINATION
  // -------------------------------------------------------------------------

  const [page, setPage] = useState(1);

  const [pageSize, setPageSize] = useState(25);

  // -------------------------------------------------------------------------
  // TABS
  // -------------------------------------------------------------------------

  const tabs = [
    ["ALL", "All My Work"],
    ["EXECUTION", "My Execution"],
    ["OWNERSHIP", "My Ownership"],
    ["APPROVALS", "My Approvals"],
    ["OVERDUE", "Overdue"],
    ["DUE_TODAY", "Due Today"],
    ["ESCALATED", "Escalated"],
    ["COMPLETED", "Completed"],
  ];

  
  const handleComments = async (work) => {
  try {
    setSelectedWork(work);

    const workspace =
      await wmsExecutorWorkspaceService
        .getExecutionWorkspace(
          work.id
        );

    setCommentWorkspace(
      workspace
    );

    setShowComments(true);

  } catch (error) {
    console.error(
      "Failed to load comments",
      error
    );
  }
};
  
  // -------------------------------------------------------------------------
  // LOAD MY WORK
  // -------------------------------------------------------------------------

  const loadMyWork = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const result =
        await wmsMyWorkService.getMyWork({
          page,
          page_size: pageSize,

          tab:
            activeTab === "ALL"
              ? ""
              : activeTab,

          search:
            filters.search.trim(),

          status:
            filters.status,

          sla_status:
            filters.sla_status,

          priority:
            filters.priority,

          work_mode:
            filters.work_mode,

          template:
            filters.template,
        });

      setData({
        ...EMPTY_DATA,

        ...result,

        summary: {
          ...EMPTY_DATA.summary,
          ...(result?.summary || {}),
        },

        attention: {
          ...EMPTY_DATA.attention,
          ...(result?.attention || {}),
        },

        work: Array.isArray(result?.work)
          ? result.work
          : [],

        my_escalations:
          Array.isArray(
            result?.my_escalations
          )
            ? result.my_escalations
            : [],

        sla_distribution:
          Array.isArray(
            result?.sla_distribution
          )
            ? result.sla_distribution
            : [],

        timeline:
          Array.isArray(result?.timeline)
            ? result.timeline
            : [],

        pagination: {
          ...EMPTY_DATA.pagination,
          ...(result?.pagination || {}),
        },
      });
    } catch (err) {
      console.error(
        "My Work API error:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load My Work."
      );

      setData(EMPTY_DATA);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    activeTab,
    filters,
  ]);

  // -------------------------------------------------------------------------
  // LOAD WHEN PAGE / FILTER / TAB CHANGES
  //
  // Small debounce prevents an API request for every individual keystroke
  // in the Search field.
  // -------------------------------------------------------------------------

  useEffect(() => {
    const timer = setTimeout(() => {
      loadMyWork();
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [loadMyWork]);

  // -------------------------------------------------------------------------
  // DISPLAYED ROWS
  //
  // IMPORTANT:
  //
  // No frontend filtering here.
  //
  // Backend has already applied:
  //   - tab
  //   - search
  //   - status
  //   - SLA status
  //   - priority
  //   - work mode
  //   - template
  //
  // Therefore data.work is already the correct page.
  // -------------------------------------------------------------------------

  const rows = useMemo(() => {
    return Array.isArray(data.work)
      ? data.work
      : [];
  }, [data.work]);

  // -------------------------------------------------------------------------
  // TEMPLATE OPTIONS
  //
  // Keep the existing UI behavior for now.
  // The backend can later return a dedicated template_options list so this
  // dropdown can contain every template independent of the current page.
  // -------------------------------------------------------------------------

  const templateOptions = useMemo(() => {
    return [
      ...new Set(
        data.work
          .map(
            (item) =>
              item.template_name
          )
          .filter(Boolean)
      ),
    ];
  }, [data.work]);

  // -------------------------------------------------------------------------
  // FILTER UPDATE
  // -------------------------------------------------------------------------

  const updateFilter = (
    key,
    value
  ) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
    }));

    // Every filter change starts from page 1.
    setPage(1);
  };

  // -------------------------------------------------------------------------
  // CLEAR FILTERS
  // -------------------------------------------------------------------------

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      sla_status: "",
      priority: "",
      work_mode: "",
      template: "",
    });

    setPage(1);
  };

  // -------------------------------------------------------------------------
  // TAB CHANGE
  // -------------------------------------------------------------------------

  const handleTabChange = (
    tab
  ) => {
    setActiveTab(tab);

    // Always start a new tab from page 1.
    setPage(1);
  };

  // -------------------------------------------------------------------------
  // PAGE CHANGE
  // -------------------------------------------------------------------------

  const changePage = (
    nextPage
  ) => {
    const totalPages =
      Number(
        data.pagination?.total_pages
      ) || 0;

    if (
      nextPage < 1 ||
      (totalPages > 0 &&
        nextPage > totalPages)
    ) {
      return;
    }

    setPage(nextPage);
  };

  // -------------------------------------------------------------------------
  // PAGE SIZE CHANGE
  // -------------------------------------------------------------------------

  const changePageSize = (
    nextPageSize
  ) => {
    const parsed =
      Number(nextPageSize);

    if (
      ![25, 50, 100].includes(
        parsed
      )
    ) {
      return;
    }

    setPageSize(parsed);

    // Changing page size always starts from page 1.
    setPage(1);
  };

  // -------------------------------------------------------------------------
  // WORK ACTION
  //
  // IMPORTANT:
  //
  // /wms/works/{id}/details is intentionally NOT used.
  // -------------------------------------------------------------------------

  const handleAction = (
    row
  ) => {
    // Approver
    if (
      row.status ===
      "WAITING_FOR_APPROVAL"
    ) {
      navigate(
        `/wms/works/${row.id}/approval`
      );

      return;
    }

    // Executor
    if (
      Array.isArray(
        row.my_role
      ) &&
      row.my_role.includes(
        "EXECUTOR"
      ) &&
      row.status !== "CLOSED" &&
      row.status !== "COMPLETED"
    ) {
      navigate(
        `/wms/works/${row.id}/execute`
      );

      return;
    }

    // No details route.
    //
    // If the user has no executable/approval action,
    // do nothing rather than opening the prohibited
    // /details route.
  };

  // -------------------------------------------------------------------------
  // REFRESH
  // -------------------------------------------------------------------------

  const handleRefresh = () => {
    loadMyWork();
  };

  // -------------------------------------------------------------------------
  // PAGINATION VALUES
  // -------------------------------------------------------------------------

  const pagination =
    data.pagination ||
    EMPTY_DATA.pagination;

  const currentPage =
    Number(
      pagination.page
    ) || page;

  const totalPages =
    Number(
      pagination.total_pages
    ) || 0;

  const totalRecords =
    Number(
      pagination.total
    ) || 0;

  const hasPrevious =
    pagination.has_previous === true ||
    currentPage > 1;

  const hasNext =
    pagination.has_next === true ||
    currentPage < totalPages;

  const firstRecord =
    totalRecords === 0
      ? 0
      : (
          (currentPage - 1) *
            pageSize
        ) + 1;

  const lastRecord =
    totalRecords === 0
      ? 0
      : Math.min(
          currentPage *
            pageSize,
          totalRecords
        );

  // -------------------------------------------------------------------------
  // UI
  // -------------------------------------------------------------------------

  return (
    <div className="my-work-page">

      {/* ======================================================
          HEADER
          ====================================================== */}

      <div className="my-work-header">

        <div className="d-flex justify-content-between align-items-center">

          <div>

            <h2>
              My Work
            </h2>

            <p>
              All your work items based
              on your role and
              responsibilities
            </p>

          </div>

          <button
            type="button"
            className="btn btn-light"
            onClick={
              handleRefresh
            }
            disabled={
              loading
            }
          >

            <i
              className={`fas ${
                loading
                  ? "fa-spinner fa-spin"
                  : "fa-sync-alt"
              } me-2`}
            />

            {loading
              ? "Loading..."
              : "Refresh"}

          </button>

        </div>

      </div>

      {/* ======================================================
          API ERROR
          ====================================================== */}

      {error && (

        <div className="alert alert-danger d-flex justify-content-between align-items-center">

          <span>

            <i className="fas fa-exclamation-circle me-2" />

            {error}

          </span>

          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={
              handleRefresh
            }
          >
            Retry
          </button>

        </div>

      )}

      {/* ======================================================
          SUMMARY
          ====================================================== */}

      <MyWorkSummaryCards
        summary={
          data.summary
        }
        onSelect={
          handleTabChange
        }
      />

      {/* ======================================================
          ATTENTION
          ====================================================== */}

      <MyWorkAttentionCards
        attention={
          data.attention
        }
        onSelect={
          handleTabChange
        }
      />

      {/* ======================================================
          MAIN GRID
          ====================================================== */}

      <div className="my-work-main-grid">

        {/* ====================================================
            LEFT
            ==================================================== */}

        <div className="my-work-main">

          {/* ==================================================
              TABS
              ================================================== */}

          <div className="my-work-tabs">

            {tabs.map(
              ([
                key,
                label,
              ]) => (

                <button
                  key={key}
                  type="button"
                  className={`my-work-tab ${
                    activeTab ===
                    key
                      ? "active"
                      : ""
                  }`}
                  onClick={() =>
                    handleTabChange(
                      key
                    )
                  }
                >
                  {label}
                </button>

              )
            )}

          </div>

          {/* ==================================================
              FILTERS
              ================================================== */}

          <div className="my-work-filter-card">

            <div className="my-work-filter-grid">

              {/* SEARCH */}

              <div className="my-work-filter-field">

                <label>
                  Search Work
                </label>

                <input
                  className="form-control"
                  placeholder="Work No / Title / Template"
                  value={
                    filters.search
                  }
                  onChange={(event) =>
                    updateFilter(
                      "search",
                      event.target.value
                    )
                  }
                />

              </div>

              {/* STATUS */}

              <div className="my-work-filter-field">

                <label>
                  Status
                </label>

                <select
                  className="form-select"
                  value={
                    filters.status
                  }
                  onChange={(event) =>
                    updateFilter(
                      "status",
                      event.target.value
                    )
                  }
                >

                  <option value="">
                    All
                  </option>

                  <option value="OPEN">
                    Open
                  </option>

                  <option value="IN_PROGRESS">
                    In Progress
                  </option>

                  <option value="WAITING_FOR_APPROVAL">
                    Waiting for Approval
                  </option>

                  <option value="CLOSED">
                    Closed
                  </option>

                  <option value="COMPLETED">
                    Completed
                  </option>

                </select>

              </div>

              {/* SLA */}

              <div className="my-work-filter-field">

                <label>
                  SLA Status
                </label>

                <select
                  className="form-select"
                  value={
                    filters.sla_status
                  }
                  onChange={(event) =>
                    updateFilter(
                      "sla_status",
                      event.target.value
                    )
                  }
                >

                  <option value="">
                    All
                  </option>

                  <option value="WITHIN_SLA">
                    Within SLA
                  </option>

                  <option value="WARNING">
                    Warning
                  </option>

                  <option value="APPROACHING_SLA">
                    Approaching SLA
                  </option>

                  <option value="BREACHED">
                    Breached
                  </option>

                  <option value="COMPLETED_WITHIN_SLA">
                    Completed - Within SLA
                  </option>

                  <option value="COMPLETED_AFTER_BREACH">
                    Completed - After Breach
                  </option>

                </select>

              </div>

              {/* PRIORITY */}

              <div className="my-work-filter-field">

                <label>
                  Priority
                </label>

                <select
                  className="form-select"
                  value={
                    filters.priority
                  }
                  onChange={(event) =>
                    updateFilter(
                      "priority",
                      event.target.value
                    )
                  }
                >

                  <option value="">
                    All
                  </option>

                  <option value="CRITICAL">
                    Critical
                  </option>

                  <option value="HIGH">
                    High
                  </option>

                  <option value="MEDIUM">
                    Medium
                  </option>

                  <option value="LOW">
                    Low
                  </option>

                </select>

              </div>

              {/* WORK MODE */}

              <div className="my-work-filter-field">

                <label>
                  Work Mode
                </label>

                <select
                  className="form-select"
                  value={
                    filters.work_mode
                  }
                  onChange={(event) =>
                    updateFilter(
                      "work_mode",
                      event.target.value
                    )
                  }
                >

                  <option value="">
                    All
                  </option>

                  <option value="ADHOC">
                    Adhoc
                  </option>

                  <option value="TEMPLATE">
                    Template
                  </option>

                </select>

              </div>

              {/* TEMPLATE */}

              <div className="my-work-filter-field">

                <label>
                  Template
                </label>

                <select
                  className="form-select"
                  value={
                    filters.template
                  }
                  onChange={(event) =>
                    updateFilter(
                      "template",
                      event.target.value
                    )
                  }
                >

                  <option value="">
                    All
                  </option>

                  {templateOptions.map(
                    (template) => (

                      <option
                        key={template}
                        value={template}
                      >
                        {template}
                      </option>

                    )
                  )}

                </select>

              </div>

              {/* CLEAR */}

              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={
                  clearFilters
                }
                title="Clear filters"
              >
                <i className="fas fa-times" />
              </button>

            </div>

          </div>

          {/* ==================================================
              TABLE
              ================================================== */}

          <MyWorkTable
            rows={rows}
            loading={loading}
            onAction={handleAction}
            onComments={handleComments}
          />
          <CommentsModal
            show={showComments}
            onClose={() => {
              setShowComments(false);
              setSelectedWork(null);
              setCommentWorkspace(null);
            }}
            comments={commentWorkspace?.comments || []}
            
            onAddComment={async (text) => {

            await wmsWorkService.addComment(
              selectedWork.id,
              {
                comment_text: text,
              }
            );

            const workDetails =
              await wmsWorkService.getWork(
                selectedWork.id
              );

            setCommentWorkspace(workDetails);
          }}
            title={
              selectedWork
                ? `Comments - ${selectedWork.work_no}`
                : "Comments"
            }
          />

          {/* ==================================================
              SERVER-SIDE PAGINATION
              ================================================== */}

          <div
            className="d-flex justify-content-between align-items-center px-2 py-2"
            style={{
              fontSize: "12px",
            }}
          >

            {/* RECORD INFORMATION */}

            <div className="text-muted">

              Showing{" "}
              <strong>
                {firstRecord}
              </strong>
              {" "}to{" "}
              <strong>
                {lastRecord}
              </strong>
              {" "}of{" "}
              <strong>
                {totalRecords}
              </strong>
              {" "}entries

            </div>

            {/* PAGINATION */}

            <div className="d-flex align-items-center gap-1">

              {/* PAGE SIZE */}

              <span
                className="text-muted me-1"
              >
                Rows:
              </span>

              <select
                className="form-select form-select-sm"
                style={{
                  width: "72px",
                }}
                value={
                  pageSize
                }
                onChange={(event) =>
                  changePageSize(
                    event.target.value
                  )
                }
              >

                <option value={25}>
                  25
                </option>

                <option value={50}>
                  50
                </option>

                <option value={100}>
                  100
                </option>

              </select>

              {/* FIRST */}

              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={
                  !hasPrevious ||
                  loading
                }
                onClick={() =>
                  changePage(1)
                }
                title="First page"
              >
                «
              </button>

              {/* PREVIOUS */}

              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={
                  !hasPrevious ||
                  loading
                }
                onClick={() =>
                  changePage(
                    currentPage - 1
                  )
                }
                title="Previous page"
              >
                ‹
              </button>

              {/* CURRENT PAGE */}

              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled
                style={{
                  minWidth: "42px",
                }}
              >
                {currentPage}
              </button>

              {/* NEXT */}

              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={
                  !hasNext ||
                  loading
                }
                onClick={() =>
                  changePage(
                    currentPage + 1
                  )
                }
                title="Next page"
              >
                ›
              </button>

              {/* LAST */}

              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={
                  !hasNext ||
                  loading
                }
                onClick={() =>
                  changePage(
                    totalPages
                  )
                }
                title="Last page"
              >
                »
              </button>

              {totalPages > 0 && (

                <span
                  className="text-muted ms-2"
                >
                  Page{" "}
                  {currentPage}
                  {" "}of{" "}
                  {totalPages}
                </span>

              )}

            </div>

          </div>

        </div>

        {/* ====================================================
            RIGHT SIDE
            ==================================================== */}

        <div>

          {/* ==================================================
              ESCALATIONS
              ================================================== */}

          <div className="my-work-widget">

            <div className="my-work-widget-head">

              <h6>

                <i className="fas fa-bell text-danger me-2" />

                My Escalations

              </h6>

              <span className="link">
                View All
              </span>

            </div>

            <div className="my-work-widget-body">

              {data.my_escalations.length ===
              0 ? (

                <div className="text-muted small py-2">
                  No escalations.
                </div>

              ) : (

                data.my_escalations.map(
                  (
                    item,
                    index
                  ) => (

                    <div
                      className="escalation-item"
                      key={
                        item.work_no ||
                        item.id ||
                        `escalation-${index}`
                      }
                    >

                      <div className="escalation-top">

                        <span className="escalation-work">
                          {item.work_no ||
                            "—"}
                        </span>

                        <span className="my-work-badge badge-sla-breached">

                          {item.level ||
                            item.escalation_level ||
                            "Escalated"}

                        </span>

                        <span className="escalation-time">

                          {item.time ||
                            item.escalated_at ||
                            ""}

                        </span>

                      </div>

                      <div className="escalation-title">

                        {item.title ||
                          item.reason ||
                          "Work escalation"}

                      </div>

                    </div>

                  )
                )

              )}

            </div>

          </div>

          {/* ==================================================
              SLA DISTRIBUTION
              ================================================== */}

          <div className="my-work-widget">

            <div className="my-work-widget-head">

              <h6>

                <i className="fas fa-chart-pie text-primary me-2" />

                SLA Distribution

              </h6>

            </div>

            <div className="my-work-widget-body">

              {data.sla_distribution.length ===
              0 ? (

                <div className="text-muted small py-2">
                  No SLA data.
                </div>

              ) : (

                data.sla_distribution.map(
                  (
                    item,
                    index
                  ) => {

                    const value =
                      Number(
                        item.value ||
                          0
                      );

                    /*
                     * Backend SLA distribution is based on the
                     * complete filtered dataset.
                     *
                     * Do NOT use data.work.length here because
                     * data.work now contains only one page.
                     */

                    const total =
                      Number(
                        data.pagination?.total
                      ) || 0;

                    const percent =
                      item.percent !==
                      undefined
                        ? Number(
                            item.percent ||
                              0
                          )
                        : total
                        ? Math.round(
                            (value /
                              total) *
                              100
                          )
                        : 0;

                    let className =
                      "sla-completed";

                    if (
                      item.label ===
                        "Within SLA" ||
                      item.name ===
                        "Within SLA"
                    ) {
                      className =
                        "sla-good";

                    } else if (
                      item.label ===
                        "Warning" ||
                      item.label ===
                        "SLA Warning" ||
                      item.name ===
                        "Warning"
                    ) {
                      className =
                        "sla-warning";

                    } else if (
                      item.label ===
                        "Breached" ||
                      item.label ===
                        "SLA Breached" ||
                      item.name ===
                        "Breached"
                    ) {
                      className =
                        "sla-bad";
                    }

                    return (

                      <div
                        className="sla-row"
                        key={
                          item.label ||
                          item.name ||
                          `sla-${index}`
                        }
                      >

                        <div className="sla-label">

                          {item.label ||
                            item.name}

                        </div>

                        <div className="sla-value">

                          {value} (
                          {percent}
                          %)

                        </div>

                        <div className="sla-track">

                          <div
                            className={`sla-fill ${className}`}
                            style={{
                              width: `${percent}%`,
                            }}
                          />

                        </div>

                      </div>

                    );
                  }
                )

              )}

            </div>

          </div>

          {/* ==================================================
              TIMELINE
              ================================================== */}

          <div className="my-work-widget">

            <div className="my-work-widget-head">

              <h6>

                <i className="fas fa-stream text-primary me-2" />

                Today's Timeline

              </h6>

              <span className="link">
                View All
              </span>

            </div>

            <div className="my-work-widget-body">

              {data.timeline.length ===
              0 ? (

                <div className="text-muted small py-2">
                  No timeline items.
                </div>

              ) : (

                data.timeline.map(
                  (
                    item,
                    index
                  ) => (

                    <div
                      className="timeline-item"
                      key={
                        item.id ||
                        `${item.time || "timeline"}-${index}`
                      }
                    >

                      <div className="timeline-time">

                        {item.time ||
                          item.due_time ||
                          "—"}

                      </div>

                      <div
                        className={`timeline-dot ${
                          item.dot ||
                          "dot-blue"
                        }`}
                      />

                      <div className="timeline-content">

                        {item.title ||
                          item.work_no ||
                          "Work"}

                      </div>

                      <span className="my-work-badge badge-progress">

                        {item.status ||
                          "Scheduled"}

                      </span>

                    </div>

                  )
                )

              )}

            </div>

          </div>

        </div>

      </div>

    </div>
  );
}